// Central role resolution. The id->name mapping used to be copy-pasted across
// Sidebar, Userbox, and User Management; this is the single source of truth.
//
// `localStorage.userRole` may hold a role id ("5"), a comma-list of ids
// ("1,2"), or already a resolved name ("Demo"). `userRolesList` is the
// { id, roles } array from API.userRoles (global state key `userRoles`).

export type RoleOption = { id: string | number; roles: string };

// Resolve the raw stored userRole into a human role name (lowercased, joined
// when multiple). Unmapped non-numeric parts are kept as-is; unmapped numeric
// ids resolve to '' (we only know it's an id we couldn't map yet).
export const resolveRoleName = (
  rawUserRole: string | null | undefined,
  userRolesList: RoleOption[] | null | undefined
): string => {
  return String(rawUserRole || '')
    .split(',')
    .map((part) => {
      const key = part.trim();
      if (!key) return '';
      const found = (userRolesList || []).find(
        (r) => String(r.id) === key
      );
      if (found) return String(found.roles);
      return /^\d+$/.test(key) ? '' : key;
    })
    .filter(Boolean)
    .join(', ')
    .toLowerCase()
    .trim();
};

// The Demo role is id "5" / name "Demo". True when the raw value is (or maps
// to) that role. Safe before `userRolesList` loads: a bare "5" still matches.
export const isDemoUser = (
  userRolesList?: RoleOption[] | null
): boolean => {
  const raw = localStorage.getItem('userRole') || '';
  const ids = raw.split(',').map((p) => p.trim());
  if (ids.includes('5')) return true;
  return resolveRoleName(raw, userRolesList).includes('demo');
};

export type RoleCategory =
  | 'superadmin'
  | 'admin'
  | 'reviewer'
  | 'annotator'
  | 'default';

// Map a raw stored userRole to a visibility/access category. This mirrors the
// Sidebar's gating so page-level guards stay in sync with what the sidebar
// shows. Unlike `resolveRoleName`, unmapped numeric ids are kept (not dropped)
// so a bare role name embedded in the raw value is still detectable.
export const resolveRoleCategory = (
  rawUserRole: string | null | undefined,
  userRolesList: RoleOption[] | null | undefined
): RoleCategory => {
  const roleName = String(rawUserRole || '')
    .split(',')
    .map((part) => {
      const key = part.trim();
      const found = (userRolesList || []).find((r) => String(r.id) === key);
      return found ? String(found.roles) : key;
    })
    .join(' ')
    .toLowerCase();

  if (roleName.includes('super')) return 'superadmin';
  if (roleName.includes('admin')) return 'admin';
  if (roleName.includes('review')) return 'reviewer';
  if (roleName.includes('annotat')) return 'annotator';
  return 'default';
};

// True when the logged-in user is a Super Admin. Reads the raw role from
// localStorage; pass `userRolesList` when the raw value is an id that needs
// resolving to the "Super Admin" name.
export const isSuperAdmin = (
  userRolesList?: RoleOption[] | null
): boolean =>
  resolveRoleCategory(localStorage.getItem('userRole'), userRolesList) ===
  'superadmin';
